import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    ProfileSectionAttribute,
    ProfileSectionResponse,
    useApiClient
} from '@/lib/api';
import { FilePlus2, Plus, Save, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import DetailsView from '../detail/DetailsView';
import SkillsView from '../skill/SkillsView';

export const DEFAULT_PROFILE_SECTIONS = [
    { title: 'Certifications', section_key: 'certifications' },
    { title: 'Publications', section_key: 'publications' },
    { title: 'Patents', section_key: 'patents' },
    { title: 'Research', section_key: 'research' },
];

type SectionDraft = {
    title: string;
    section_key?: string | null;
    attributes: ProfileSectionAttribute[];
};

interface ProfileSectionEditorProps {
    section: ProfileSectionResponse;
    onSectionUpdated: (section: ProfileSectionResponse) => void;
    onSectionDeleted: (sectionId: string) => void;
}

export const ProfileSectionEditor: React.FC<ProfileSectionEditorProps> = ({
    section,
    onSectionUpdated,
    onSectionDeleted,
}) => {
    const { api } = useApiClient();
    const [draft, setDraft] = useState<SectionDraft>({
        title: section.title,
        section_key: section.section_key,
        attributes: section.attributes || [],
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setDraft({
            title: section.title,
            section_key: section.section_key,
            attributes: (section.attributes || []).map(attribute => ({
                label: attribute.label,
                value: attribute.value,
            })),
        });
    }, [section]);

    const handleAttributeChange = (
        index: number,
        key: keyof ProfileSectionAttribute,
        value: string
    ) => {
        setDraft(prev => ({
            ...prev,
            attributes: prev.attributes.map((attribute, attributeIndex) => (
                attributeIndex === index
                    ? { ...attribute, [key]: value }
                    : attribute
            )),
        }));
    };

    const handleAddAttribute = (label = '', value = '') => {
        setDraft(prev => ({
            ...prev,
            attributes: [...prev.attributes, { label, value }],
        }));
    };

    const handleRemoveAttribute = (index: number) => {
        setDraft(prev => ({
            ...prev,
            attributes: prev.attributes.filter((_, attributeIndex) => attributeIndex !== index),
        }));
    };

    const handleSaveSection = async () => {
        if (!draft.title.trim()) {
            toast.error('Section title is required.');
            return;
        }

        setIsSaving(true);
        try {
            const updated = await api.updateProfileSection(section.id, {
                title: draft.title.trim(),
                section_key: draft.section_key ?? 'custom',
                attributes: draft.attributes,
            });
            onSectionUpdated(updated);
            toast.success('Section updated.');
        } catch (error) {
            console.error('Failed to update profile section', error);
            toast.error('Unable to save section right now.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteSection = async () => {
        setIsSaving(true);
        try {
            await api.deleteProfileSection(section.id);
            onSectionDeleted(section.id);
        } catch (error) {
            console.error('Failed to delete profile section', error);
            toast.error('Unable to delete section right now.');
        } finally {
            setIsSaving(false);
        }
    };

    const usedLabels = new Set(
        (draft.attributes || [])
            .map(attribute => attribute.label.trim().toLowerCase())
            .filter(Boolean)
    );
    const availableSuggestions = (section.suggested_attributes || [])
        .filter(label => !usedLabels.has(label.trim().toLowerCase()));

    return (
        <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                        <div className="space-y-2">
                            <Label htmlFor={`section-title-${section.id}`}>Section Title</Label>
                            <Input
                                id={`section-title-${section.id}`}
                                value={draft.title}
                                onChange={event => setDraft(prev => ({
                                    ...prev,
                                    title: event.target.value,
                                }))}
                                disabled={isSaving}
                            />
                        </div>
                        <CardDescription>
                            Add custom fields, supporting details, and related skills for this section.
                        </CardDescription>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={handleDeleteSection}
                        disabled={isSaving}
                    >
                        <Trash2 className="mr-1 h-4 w-4" />
                        Delete
                    </Button>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-slate-900">Section Fields</h4>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAddAttribute()}
                                disabled={isSaving}
                            >
                                <Plus className="mr-1 h-4 w-4" />
                                Add Field
                            </Button>
                        </div>
                        {availableSuggestions.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {availableSuggestions.map(label => (
                                    <Button
                                        key={label}
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleAddAttribute(label)}
                                        disabled={isSaving}
                                    >
                                        <Plus className="mr-1 h-3 w-3" />
                                        {label}
                                    </Button>
                                ))}
                            </div>
                        ) : null}
                        {draft.attributes.length === 0 ? (
                            <p className="text-sm italic text-slate-500">
                                No custom fields yet.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {draft.attributes.map((attribute, index) => (
                                    <div
                                        key={`${section.id}-attribute-${index}`}
                                        className="grid gap-2 rounded-lg border border-slate-200 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]"
                                    >
                                        <Input
                                            value={attribute.label}
                                            onChange={event => handleAttributeChange(index, 'label', event.target.value)}
                                            placeholder="Field name"
                                            disabled={isSaving}
                                        />
                                        <Input
                                            value={attribute.value}
                                            onChange={event => handleAttributeChange(index, 'value', event.target.value)}
                                            placeholder="Field value"
                                            disabled={isSaving}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() => handleRemoveAttribute(index)}
                                            disabled={isSaving}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end">
                        <Button
                            onClick={handleSaveSection}
                            disabled={isSaving || !draft.title.trim()}
                        >
                            <Save className="mr-1 h-4 w-4" />
                            Save Section
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <div>
                <h4 className="mb-2 text-sm font-semibold text-slate-900">Descriptions</h4>
                <DetailsView profileSectionId={section.id} />
            </div>
            <div>
                <h4 className="mb-2 text-sm font-semibold text-slate-900">Skills</h4>
                <SkillsView profileSectionId={section.id} />
            </div>
        </div>
    );
};

interface ProfileSectionCreatePanelProps {
    existingSectionKeys: Set<string | null | undefined>;
    onSectionCreated: (section: ProfileSectionResponse) => void;
}

export const ProfileSectionCreatePanel: React.FC<ProfileSectionCreatePanelProps> = ({
    existingSectionKeys,
    onSectionCreated,
}) => {
    const { api } = useApiClient();
    const [customTitle, setCustomTitle] = useState('');
    const [customFields, setCustomFields] = useState<ProfileSectionAttribute[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    const handleCreateSection = async (
        title: string,
        sectionKey?: string | null,
        attributes?: ProfileSectionAttribute[]
    ) => {
        const normalizedTitle = title.trim();
        if (!normalizedTitle) {
            toast.error('Section name is required.');
            return;
        }

        setIsSaving(true);
        try {
            const created = await api.createProfileSection({
                title: normalizedTitle,
                section_key: sectionKey ?? 'custom',
                attributes,
            });
            setCustomTitle('');
            setCustomFields([]);
            onSectionCreated(created);
        } catch (error) {
            console.error('Failed to create profile section', error);
            toast.error('Unable to create section right now.');
        } finally {
            setIsSaving(false);
        }
    };

    const availableDefaults = DEFAULT_PROFILE_SECTIONS.filter(
        section => !existingSectionKeys.has(section.section_key)
    );

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold sm:text-xl">Create Profile Section</h3>
                <p className="text-sm text-slate-600">
                    Start from a common template or define your own section fields.
                </p>
            </div>

            <div className="space-y-3">
                <Label>Common Sections</Label>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {availableDefaults.map(section => (
                        <Button
                            key={section.section_key}
                            variant="outline"
                            className="h-auto justify-start px-4 py-3 text-left"
                            onClick={() => handleCreateSection(section.title, section.section_key)}
                            disabled={isSaving}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            <span>{section.title}</span>
                        </Button>
                    ))}
                </div>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-base">Custom Section</CardTitle>
                    <CardDescription>
                        Name the section and choose any fields it should track, like Link, Patent #, Issuer, or Date.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="custom-section-title">Section Name</Label>
                        <Input
                            id="custom-section-title"
                            value={customTitle}
                            onChange={event => setCustomTitle(event.target.value)}
                            placeholder="Examples: Speaking Engagements, Awards, Clinical Research"
                            disabled={isSaving}
                        />
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label>Fields</Label>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCustomFields(prev => [...prev, { label: '', value: '' }])}
                                disabled={isSaving}
                            >
                                <Plus className="mr-1 h-4 w-4" />
                                Add Field
                            </Button>
                        </div>
                        {customFields.length === 0 ? (
                            <p className="text-sm italic text-slate-500">
                                No fields yet. You can add them now or later.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {customFields.map((field, index) => (
                                    <div
                                        key={`custom-field-${index}`}
                                        className="grid gap-2 rounded-lg border border-slate-200 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]"
                                    >
                                        <Input
                                            value={field.label}
                                            onChange={event => setCustomFields(prev => prev.map((current, currentIndex) => (
                                                currentIndex === index
                                                    ? { ...current, label: event.target.value }
                                                    : current
                                            )))}
                                            placeholder="Field name"
                                            disabled={isSaving}
                                        />
                                        <Input
                                            value={field.value}
                                            onChange={event => setCustomFields(prev => prev.map((current, currentIndex) => (
                                                currentIndex === index
                                                    ? { ...current, value: event.target.value }
                                                    : current
                                            )))}
                                            placeholder="Optional value"
                                            disabled={isSaving}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() => setCustomFields(prev => prev.filter((_, currentIndex) => currentIndex !== index))}
                                            disabled={isSaving}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <Button
                        onClick={() => handleCreateSection(customTitle, 'custom', customFields)}
                        disabled={isSaving || !customTitle.trim()}
                    >
                        <FilePlus2 className="mr-1 h-4 w-4" />
                        Create Section
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};
